import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader } from "@/components/common";
import { saludo } from "@/lib/textos";
import { Card } from "@/components/ui/card";
import { LayoutList, Files, ScanLine, BarChart3, ShieldAlert, ArrowRight, FilePlus2, Users, Car, UserCog, Truck, Building2, Warehouse, Package } from "lucide-react";

const TILES = [
  { to: "/albaranes", title: "Selección de albaranes", desc: "Filtra y elige albaranes desde Sage (VenturaAPI).", icon: LayoutList, roles: ["empresa", "gestoria"] },
  { to: "/decas/manual", title: "DeCA manual", desc: "Emite un DeCA para clientes sin Sage: destino, transporte y albaranes.", icon: FilePlus2, roles: ["empresa", "gestoria"] },
  { to: "/transportistas", title: "Transportistas", desc: "Crea y edita las agencias de transporte de tu empresa.", icon: Truck, roles: ["empresa", "gestoria"] },
  { to: "/cargadores", title: "Cargadores", desc: "Edita raz" + String.fromCharCode(0xf3) + "n social, NIF y domicilio de origen si cambia o hubo un error.", icon: Warehouse, roles: ["empresa", "gestoria"] },
  { to: "/clientes", title: "Clientes", desc: "Crea y edita los destinatarios para el DeCA manual.", icon: Building2, roles: ["empresa", "gestoria"] },
  { to: "/proveedores", title: "Proveedores", desc: "Origen del DeCA a proveedor: raz" + String.fromCharCode(0xf3) + "n social, NIF y domicilio.", icon: Package, roles: ["empresa", "gestoria"], need: "deca_proveedores" },
  { to: "/conductores", title: "Conductores", desc: "Conductores y correo de env" + String.fromCharCode(0xed) + "o por transportista, para el DeCA.", icon: Users, roles: ["empresa", "gestoria"], need: "sage_conductores" },
  { to: "/matriculas", title: "Matrículas", desc: "Camión, furgoneta y remolque por agencia de transporte, para el DeCA.", icon: Car, roles: ["empresa", "gestoria"] },
  { to: "/decas",     title: "Histórico de DeCA",       desc: "Documentos emitidos, versiones y descarga.",     icon: Files, roles: ["empresa","gestoria","conductor"] },
  { to: "/conductor", title: "Modo conductor",          desc: "Vista simplificada con QR grande para inspección.", icon: ScanLine, roles: ["conductor"] },
  { to: "/informes",  title: "Informes",                desc: "Totales vigentes, kg, bultos, gr" + String.fromCharCode(0xe1) + "fico por periodo y Excel.", icon: BarChart3, roles: ["empresa","gestoria"] },
  { to: "/auditoria", title: "Auditoría",               desc: "Eventos de sistema, cumplimiento y trazabilidad.", icon: ShieldAlert, roles: ["admin", "empresa", "gestoria"] },
  { to: "/empresas",  title: "Empresas",                desc: "C" + String.fromCharCode(0xf3) + "digo interno, l" + String.fromCharCode(0xed) + "mites, Sage y URL de la API.", icon: Building2, roles: ["admin"] },
  { to: "/usuarios",  title: "Usuarios",                desc: "Crear usuarios, asignar rol y empresa, y cambiar contrase" + String.fromCharCode(0xf1) + "a.", icon: UserCog, roles: ["admin"] },
];

export default function HomePage() {
  const { user } = useAuth();
  const tiles = TILES.filter(t => t.roles.includes(user.rol) && (!t.need || user?.[t.need]));
  return (
    <div>
      <PageHeader
        title={saludo(user.nombre || user.email)}
        subtitle={user.rol === "admin"
          ? "Solo gesti" + String.fromCharCode(0xf3) + "n de empresas, usuarios y auditor" + String.fromCharCode(0xed) + "a."
          : "Panel de control. Genera y gestiona documentos electr" + String.fromCharCode(0xf3) + "nicos del transporte."}
        testid="page-home"
      />
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link to={t.to} key={t.to} data-testid={`tile-${t.to.slice(1)}`}>
              <Card className="p-5 h-full transition-colors hover:border-blue-700 hover:shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-blue-700" strokeWidth={1.75}/>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400"/>
                </div>
                <div className="mt-4">
                  <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>{t.title}</div>
                  <div className="text-sm text-slate-500 mt-1">{t.desc}</div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {user.rol === "admin" ? null : (
      <div className="mt-8 border border-slate-200 rounded-md p-4 bg-white">
        <div className="label-tag mb-1">Cumplimiento</div>
        <div className="text-sm text-slate-700">
          DeCA Web genera <b>PDF nativos</b> con <b>QR de descarga directa sin auth</b>, conforme a la <span className="font-mono">Resolución de 5/6/2026 (BOE-A-2026-12784)</span>. Trazabilidad, versionado y auditoría integradas.
        </div>
      </div>
      )}
    </div>
  );
}
